import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ArrowLeft, ShieldCheck } from "lucide-react";
import { SITE } from "@/lib/site";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Auth({ mode = "login" }) {
  const isRegister = mode === "register";
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (isRegister) {
        await register({ name: form.name, email: form.email, phone: form.phone, password: form.password });
      } else {
        await login(form.email, form.password);
      }
      navigate("/portal");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Unable to sign in. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Brand panel */}
      <div className="relative hidden lg:block bg-[#0b2a5b] grain overflow-hidden">
        <img src={SITE.images.hero} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#061a3a] via-[#0b2a5b]/70 to-[#0b2a5b]/40" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <img src={SITE.logo} alt="Sagos" className="h-12 w-auto object-contain bg-white/95 rounded-lg p-2 self-start" />
          <div>
            <h2 className="font-serif text-4xl text-white leading-tight max-w-md">
              Your wealth, in view. Anytime, anywhere.
            </h2>
            <p className="mt-4 text-slate-300 max-w-md leading-relaxed">
              Access your reports, appointments and secure messages with your Sagos advisor
              from one private portal.
            </p>
            <div className="mt-6 flex items-center gap-2 text-sm text-[#8fb4f5]">
              <ShieldCheck size={18} /> Bank-grade encryption & fiduciary care.
            </div>
          </div>
        </div>
      </div>

      {/* Form panel */}
      <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-20 py-12">
        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="w-full max-w-md mx-auto"
        >
          <Link to="/" data-testid="auth-back-home" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-primary transition-colors mb-8">
            <ArrowLeft size={16} /> Back to home
          </Link>

          <img src={SITE.logo} alt="Sagos" className="h-10 w-auto object-contain mb-8 lg:hidden" />

          <h1 className="font-serif text-4xl text-slate-900">{isRegister ? "Become a client" : "Client login"}</h1>
          <p className="mt-2 text-slate-500">
            {isRegister ? "Create your secure Sagos portal account." : "Welcome back. Access your private portal."}
          </p>

          {error && (
            <div data-testid="auth-error" className="mt-6 rounded-lg bg-destructive/10 border border-destructive/30 text-destructive text-sm px-4 py-3">
              {error}
            </div>
          )}

          <form onSubmit={submit} className="mt-7 space-y-5" data-testid="auth-form">
            {isRegister && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="name">Full name</Label>
                  <Input id="name" data-testid="auth-name" required value={form.name} onChange={set("name")} placeholder="Jane Doe" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone (optional)</Label>
                  <Input id="phone" data-testid="auth-phone" value={form.phone} onChange={set("phone")} placeholder="(555) 000-0000" />
                </div>
              </>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="auth-email" type="email" required value={form.email} onChange={set("email")} placeholder="you@email.com" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="auth-password" type="password" required value={form.password} onChange={set("password")} placeholder="••••••••" />
            </div>

            <button
              type="submit"
              data-testid="auth-submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-full bg-primary text-primary-foreground font-medium hover:bg-[#0b2a5b] transition-colors duration-200 disabled:opacity-60"
            >
              {loading && <Loader2 size={18} className="animate-spin" />}
              {isRegister ? "Create account" : "Sign in"}
            </button>
          </form>

          <p className="mt-6 text-sm text-slate-500">
            {isRegister ? "Already have an account? " : "Don't have an account yet? "}
            <Link
              to={isRegister ? "/login" : "/register"}
              data-testid="auth-switch"
              className="font-medium text-primary hover:underline"
            >
              {isRegister ? "Sign in" : "Become a client"}
            </Link>
          </p>

          {!isRegister && (
            <p className="mt-6 text-xs text-slate-400 border-t border-border pt-4">
              Demo access — email <span className="font-mono text-slate-600">client@sagos.consulting</span> · password <span className="font-mono text-slate-600">Client2016!</span>
            </p>
          )}
        </motion.div>
      </div>
    </div>
  );
}
